import { type RefObject, useEffect } from 'react';
import {
  type ActionCreatorWithoutPayload,
  dispatchAction,
} from '@ardoq/rxbeach';
import {
  clearSubscriptions,
  subscribeToAction,
} from 'streams/utils/streamUtils';
import type { AqBlocks } from 'tabview/visualizations/aq-blocks/types';

/** The view's own zoom actions, dispatched by its view-pane header buttons. */
export type ZoomActions = {
  zoomIn: ActionCreatorWithoutPayload;
  zoomOut: ActionCreatorWithoutPayload;
  zoomFit: ActionCreatorWithoutPayload;
};

type ViewPaneZoomActions = {
  zoomIn: VoidFunction;
  zoomOut: VoidFunction;
  optimizeLayout: VoidFunction;
};

/** The view-pane header callbacks that dispatch a view's zoom actions; fit sits in the optimize slot. */
export const toViewPaneZoomActions = ({
  zoomIn,
  zoomOut,
  zoomFit,
}: ZoomActions): ViewPaneZoomActions => ({
  zoomIn: () => dispatchAction(zoomIn()),
  zoomOut: () => dispatchAction(zoomOut()),
  optimizeLayout: () => dispatchAction(zoomFit()),
});

/** Forwards a view's zoom actions to the `<aq-blocks>` element it mounts. */
export const useZoomActions = (
  elementRef: RefObject<Pick<
    AqBlocks,
    'zoomIn' | 'zoomOut' | 'zoomFit'
  > | null>,
  { zoomIn, zoomOut, zoomFit }: ZoomActions
): void => {
  useEffect(() => {
    const subscriptions = [
      subscribeToAction(zoomIn, () => elementRef.current?.zoomIn()),
      subscribeToAction(zoomOut, () => elementRef.current?.zoomOut()),
      subscribeToAction(zoomFit, () => elementRef.current?.zoomFit()),
    ];
    return () => {
      clearSubscriptions(subscriptions);
    };
  }, [elementRef, zoomIn, zoomOut, zoomFit]);
};
